// core
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';

// campfire
import { AnalyticsService, LoggerService, PartnerBrandService, IntercomService } from '@campfire-nx/api';

// portal
import { environment } from '@open-portal-env';

// services
import { PortalRedirectService } from '@open-portal-app/core/services/portal-redirect.service';
import { UserDataService } from '@open-portal-app/core/services/user-data.service';
import { ProductDetailsService } from '@open-portal-app/core/services/product-details.service';
import { AuthService } from '@open-portal-app/core/services/auth.service';
import { AppointmentService, IAppointmentDetails } from '@open-portal-app/appointments/services/appointment.service';
import { RequestService } from '@open-portal-app/request/services/request.service';
import { HelpInterstitialService } from './help-interstitial.service';
import { OdoReadingService } from '@open-portal-app/policy/odo-reading/services/odo-reading.service';

// constants
import { HELP_CONSTANTS, APPOINTMENT_BOOKING_TOPICS, ASSISTED_SUPPORT_REQUEST_TOPICS } from '../constants/help.constants';
import { COMMON_CONSTANTS } from '@open-portal-app/core/constants/common.constants';
import { MOBILE_CONSTANTS } from '@open-portal-app/core/constants/mobile.constants';

@Injectable({
  providedIn: 'root'
})
export class HelpService {
  observeHelp!: Observable<any>;
  private helpSubject!: Subject<any>;
  actionAfterReference!: string;
  isRaptorFlow!: boolean;
  isLandingPage!: boolean;
  raptorSource!: string;
  odoRequiredPolicies!: any[];
  isPrePopulated!: boolean;
  returnUrl!: string | undefined;

  constructor(
    private router: Router,
    private portalRedirectService: PortalRedirectService,
    private userDataService: UserDataService,
    private loggerService: LoggerService,
    private analyticsService: AnalyticsService,
    private helpInterstitialService: HelpInterstitialService,
    private partnerBrandService: PartnerBrandService,
    private productDetailsService: ProductDetailsService,
    private authService: AuthService,
    private appointmentService: AppointmentService,
    private requestService: RequestService,
    private intercomService: IntercomService,
    private odoReadingService: OdoReadingService
  ) {
    this.helpSubject = new Subject<any>();
    this.observeHelp = this.helpSubject.asObservable();
  }

  helpflow = this.deepCopy(HELP_CONSTANTS.initialSteps);
  partnerSlug = this.partnerBrandService.getAttribute('slug', 'partner');
  intercomData = this.partnerBrandService.getAttribute('intercom', 'partner');
  isIntercomEnabled = this.intercomData?.intercomAppId;

  back() {
    if (this.helpflow.returnUrl && this.helpflow.activeStepIndex === 1) {
      const url = decodeURIComponent(this.helpflow.returnUrl);
      this.isPrePopulated = false;
      this.helpflow.returnUrl = undefined;
      this.saveHelpFlowToSessionStorage();

      window.location.href = url;
      return;
    }

    // If going back from help-contact will have to go to specific step that lead to contact page
    if (this.helpflow.preContactIndex || this.helpflow.preContactIndex === 0) {
      this.helpflow.activeStepIndex = this.helpflow.preContactIndex;
      delete this.helpflow.preContactIndex;
    } else {
      // activeStepIndex needs to navigate back 2 steps if it is currently on 3
      const stepsToGoBack = this.helpflow.activeStepIndex === 3 ? 2 : 1;
      this.helpflow.activeStepIndex -= stepsToGoBack;
    }

    // Save updated help flow state in sessionStorage. This is to handle a refresh
    this.saveHelpFlowToSessionStorage();

    // This indicates to the subscribers that help service has got a change
    this.helpSubject.next(this.helpflow.activeStepIndex);
  }

  next(selectedAnswer?: string, selectedChoiceAction?: string) {
    const isAnswerChanged = selectedAnswer !== this.helpflow.steps[this.helpflow.activeStepIndex].answer;

    // Redirect to bad request page if there is no companyInfo, eg. no Handover
    if (!this.partnerBrandService.getAttribute('companyInfo', 'partner')) {
      this.router.navigate(['/bad-request']);
      return;
    }

    if (isAnswerChanged && !this.isPrePopulated) {
      this.resetSubsequentSteps();
    }

    // Save user's selection as "answer" in helpflow.steps
    this.helpflow.steps[this.helpflow.activeStepIndex].answer = selectedAnswer;

    // Get userSlug for analytics
    if (!this.helpflow.userSlug && this.helpflow.steps[this.helpflow.activeStepIndex].slug === 'reference-list') {
      const referenceAnswer = this.helpflow.steps.find((step: any) => step.slug === 'reference-list')?.answer as any;

      this.userDataService.getUserData()
        .then((response: any) => {
          return this.getFilteredPolicy(referenceAnswer.slug, response.policies);
        })
        .then((policyData) => {
          this.helpflow.userSlug = policyData ? (policyData.user_slug || policyData.user?.uuid) : null;
          this.logEvent(
            'submit-step',
            'next',
            this.helpflow.steps[this.helpflow.activeStepIndex].slug,
            this.helpflow.steps[this.helpflow.activeStepIndex].answer
          );
        })
    } else {
      this.logEvent(
        'submit-step',
        'next',
        this.helpflow.steps[this.helpflow.activeStepIndex].slug,
        this.helpflow.steps[this.helpflow.activeStepIndex].answer
      );
    }

    // Reset actionAfterReference when customer intent is reset/changed
    if (this.helpflow.steps[this.helpflow.activeStepIndex].slug === 'customer-intent') {
      this.actionAfterReference = '';
    }

    this.executeNextAction(selectedChoiceAction);
  }

  getHelpflow() {
    // Check if sessionStorage has a helpflow variable.
    //  This is to pre-populate user's answers if user has done a refresh
    let helpflowFromSession = window.sessionStorage.getItem('helpflow');
    if (helpflowFromSession) {
      this.helpflow = JSON.parse(helpflowFromSession);
    }

    // This either returns helpflow state with user's answers from sessionStorage
    //  OR returns default helpflow state from service variable
    return this.helpflow;
  }

  // If user goes back and changes answers, answers of subsequent steps must be reset
  resetSubsequentSteps() {
    for (var index = this.helpflow.activeStepIndex + 1; index < this.helpflow.steps.length; index++) {
      this.helpflow.steps[index].answer = '';
    }
  }

  // If there are help answer provided via query params skip steps accordingly
  // This will currently work for steps subject and intent
  prePopulateSupportSteps(queryParams: { [key: string]: any }) {
    const raptorFlows = COMMON_CONSTANTS.raptorFlows;
    this.isPrePopulated = true;
    this.isRaptorFlow = (
      typeof queryParams.flow === 'string' &&
      raptorFlows.indexOf(queryParams.flow) !== -1
    ) || false;
    let relevantAction = '';

    if (this.isRaptorFlow) {
      this.raptorSource = queryParams.flow;
    }

    for (const [key, value] of Object.entries(queryParams)) {
      const paramChoices = HELP_CONSTANTS.step_options.find(option => {
        if (key === 'customer-intent') {
          return option.key === key + '_' + queryParams.subject
        } else {
          return option.key === key
        }
      })?.choices || [];

      paramChoices.forEach((choice:any) => {
        if (choice.key === value) {
          relevantAction = choice.action;
        }
      });

      const isParamValid = paramChoices.some(choice => choice.key === value);
      const stepIndex = this.helpflow.steps.findIndex((step: any) => step.slug === key);

      if (
        isParamValid &&
        this.helpflow.activeStepIndex === stepIndex &&
        this.helpflow.steps[stepIndex].answer === ''
      ) {
        this.helpflow.steps[stepIndex].answer = value;

        if (queryParams['customer-intent']) {
          this.helpflow.steps.forEach((step: any) => {
            if (step.slug === 'customer-intent') {
              step.answer = queryParams['customer-intent'];
            }
          })
        }

        if (queryParams['reference-list']) {
          this.helpflow.steps.forEach((step: any) => {
            if (step.slug === 'reference-list') {
              step.answer = {
                ...step.answer,
                slug: queryParams['reference-list']
              };
            }
          });
        }

        if (queryParams['product-class']) {
          this.helpflow.steps.forEach((step: any) => {
            if (step.slug === 'reference-list') {
              step.answer = {
                ...step.answer,
                class: queryParams['product-class']
              };
            }
          });
        }

        if (queryParams['return-url']) {
          this.helpflow.returnUrl = queryParams['return-url'];
        }

        if (stepIndex === 0 && (this.isRaptorFlow || this.isLandingPage)) {
          if (this.authService.isAuthenticated()) {
            const updatedSteps = this.helpflow.steps.filter((steps : any) => steps.type !== 'auth');
            this.helpflow.steps = updatedSteps;
          }
          this.next(value, 'bookAppointment');
        } else {
          this.helpflow.activeStepIndex += 1;
          window.sessionStorage.setItem('helpflow', JSON.stringify(this.helpflow));

          // If user starts unauthenticated journey is Support flow, user is sent to authenticate
          //  to Auth0 sometimes. After authenticating user comes back to Support but earlier
          //  context is lost & is reset in this function.
          // At this point we need to make sure 'actionAfterReference' is set, if it was relevant
          //  for set customer intent. To ensure right action takes place after reference selection
          if (relevantAction) {
            this.actionAfterReference = relevantAction.split('-')[1] || '';
          }
        }
      }
    }
  }

  executeNextAction(selectedChoiceAction: any) {
    let policyDetail: any;
    switch (selectedChoiceAction) {
      case 'endorse': this.goToEndorsementFlow(); break;
      case 'changeRenewalFlag': this.goToRenewalFlagFlow(); break;
      case 'bookAppointment':
        const subject = this.getAnswerByStepSlug('subject');
        const customerIntent = this.getAnswerByStepSlug('customer-intent');
        const referenceAnswer = this.getAnswerByStepSlug('reference-list');
        const isAuthenticated = this.authService.isAuthenticated();

        // Redirect to base domain to start a new quote if not from quote flow, eg. no quote slug
        if (subject === 'quote' && !referenceAnswer?.slug) {

          window.location.assign(`https://${this.partnerBrandService.getAttribute('companyInfo', 'partner')?.BASE_DOMAIN}`);
          return;
        }

        if (isAuthenticated && subject !== 'quote' && referenceAnswer?.slug) {
          let currentEventLogic = 'getUserData';

          this.userDataService.getUserData()
            .then((response: any) => {
              currentEventLogic = 'getFilteredPolicy';
              let policySlug: string;

              if (customerIntent === 'existing-claim') {
                const { claims } = response;
                const claim = claims.find((claim: any) => claim.slug === referenceAnswer.slug);

                policySlug = claim.policy_slug;
              } else {
                policySlug = referenceAnswer.slug;
              }

              return this.getFilteredPolicy(policySlug, response.policies);
            })
            .then((policyData) => {
              currentEventLogic = 'getProductDetails';
              policyDetail = policyData;
              return this.productDetailsService.get(policyData.product.slug);
            })
            .then((productDetails) => {
              const isRenewalEnabled = productDetails.is_self_serviced_prerenewal_enabled;
              const isAppointmentEnabled = productDetails.is_appointment_enabled;

              this.helpflow.selectedPolicyNumber = policyDetail.policy_number;
              this.helpflow.selectedPolicyProductClass = policyDetail.product?.class;

              // Deal with appointment anomalies here
              if (policyDetail.product?.class === 'travel') {
                // Travel customers go to contact page, no appointments
                currentEventLogic = 'goToContactPage';
                this.goToContactPage();
              } else if (this.canSelfServeRenewal(policyDetail, isRenewalEnabled)) {
                this.goToSelfService();
              } else if (isAppointmentEnabled) {
                currentEventLogic = 'goToBookAppointment';

                this.goToBookAppointment(customerIntent, undefined, policyDetail);
              } else if (productDetails.class === 'mobile' && this.isIntercomEnabled) {
                // Mobile Customers go to intercom, no appointments
                this.intercomService.show();
                return;
              } else {
                currentEventLogic = 'goToContactPage';
                this.goToContactPage();
              }
            })
            .catch((err) => {
              this.loggerService.error('HelpService', 'executeNextAction', { error: err }, currentEventLogic);
            });
        } else {
          // Checking Appointment flag on the Product settings
          // otherwise fall back to Partner settings.
          // - Product config will always take precedence over Partner settings.
          let isAppointmentEnabled: boolean;

          if (referenceAnswer?.slug) {
            isAppointmentEnabled = this.partnerBrandService.data.partner.product?.is_appointment_enabled;
          } else {
            isAppointmentEnabled = !!this.partnerBrandService.data.partner.isAppointmentEnabled;
          }

          isAppointmentEnabled ? this.goToBookAppointment(customerIntent) : this.goToContactPage();
        }
        break;
      case 'selfServe': this.goToSelfService(); break;
      case 'flowRef-selfServe':
      case 'flowRef-endorse':
      case 'flowRef-bookAppointment':
        // In some scenarios we want to continue in flow till reference step(flowRef)
        //  but then need to perform an action after reference is captured
        //  At this point we save the action required after reference step
        this.actionAfterReference = selectedChoiceAction.split('-')[1];
        this.goToNextFlowStep();
        break;
      case 'smartFlightDelay':
        this.goToSmartFlightDelay();
        break;
      case 'flow':
      default:
        // Need to perform an action(saved earlier) after reference is captured
        if (
          this.helpflow.steps[this.helpflow.activeStepIndex].slug === 'reference-list' &&
          this.actionAfterReference && this.actionAfterReference !== ''
        ) {
          this.executeNextAction(this.actionAfterReference);
        } else {
          this.goToNextFlowStep();
        }
        break;
    }
  }

  goToNextFlowStep() {
    // We have an exceptional behaviour for odometer reading.
    // 'Enter Odometer reading' in subject question, is the only scenario where we skip customer-intent question,
    //   stay in the flow and directly go to reference list
    const subject = this.getAnswerByStepSlug('subject');
    if (subject === 'odo') {
      // Remove 'customer-intent' step
      const updatedSteps = this.helpflow.steps.filter((steps : any) => steps.slug !== 'customer-intent');
      this.helpflow.steps = updatedSteps;
    }

    this.helpflow.activeStepIndex += 1;

    // Save updated help flow state in sessionStorage. This is to handle a refresh
    this.saveHelpFlowToSessionStorage();

    // This indicates to the subscribers that help service has got a change
    this.helpSubject.next(this.helpflow.activeStepIndex);
    window.scrollTo(0, 0);
  }

  goToRenewalFlagFlow() {
    const referenceAnswer = this.getAnswerByStepSlug('reference-list');

    this.userDataService.getUserData()
      .then((response: any) => {
        return this.getFilteredPolicy(referenceAnswer.slug, response.policies);
      })
      .then((policyData) => {
        return this.productDetailsService.get(policyData.product.slug);
      })
      .then((productDetails) => {
        this.router.navigate([`/policy/${productDetails.class}/renewal-flag/${referenceAnswer.slug}`])
      })
  }

  goToEndorsementFlow() {
    const referenceAnswer = this.getAnswerByStepSlug('reference-list');
    const customerIntent = this.getAnswerByStepSlug('customer-intent');
    let currentEventLogic = 'getUserData';
    let userClaims: any;

    this.userDataService.getUserData()
      .then((response: any) => {
        currentEventLogic = 'getFilteredPolicy';
        userClaims = response.claims;
        return this.getFilteredPolicy(referenceAnswer.slug, response.policies);
      })
      .then((policyData) => {
        currentEventLogic = 'getProductDetails';
        this.helpflow.selectedPolicyNumber = policyData.policy_number;
        this.helpflow.selectedPolicyProductClass = policyData.product?.class;
        return this.productDetailsService.get(policyData.product.slug);
      })
      .then((productDetails) => {
        const isRaptorPolicy = productDetails.is_raptorial;
        const isAppointmentEnabled = productDetails.is_appointment_enabled;
        const isEndorsementEnabled = productDetails.is_self_serviced_endorsement_enabled;
        const hasActiveClaimsOnTravel = this.hasActiveClaimsOnTravel(referenceAnswer.slug, userClaims);

        if (!isEndorsementEnabled || hasActiveClaimsOnTravel) {
          currentEventLogic = 'goToBookAppointment';

          if (isAppointmentEnabled) {
            const payload = {
              ...APPOINTMENT_BOOKING_TOPICS[customerIntent],
              entity: `${isRaptorPolicy ? 'raptor' : productDetails.class}-policy`,
              entity_ref: referenceAnswer.slug
            }

            this.goToBookAppointment(customerIntent, payload);
          } else if (productDetails.class === 'mobile' && this.isIntercomEnabled) {
            // Mobile Customers go to intercom, no endorsements, no appointments
            this.intercomService.show();
            return;
          } else {
            this.interstitialDelay().then(() => this.goToContactPage());
          }
        } else if (isRaptorPolicy && isEndorsementEnabled) {
          currentEventLogic = 'raptorEndorsement';

          this.resetHelpflow();

          this.showInterstitial('selfService');
          this.interstitialDelay().then(() => {
            this.logEvent(
              'off-ramp',
              'goToEndorsementFlow',
              'endorsement',
              this.getHelpContext()
            );

            this.router.navigate([`/endorsement/${referenceAnswer.slug}`]);
          });
        } else {
          currentEventLogic = 'goToPortalV1ForPolicyUpdate';

          this.resetHelpflow();
          this.showInterstitial('selfService');
          this.interstitialDelay().then(() => {
            this.logEvent(
              'off-ramp',
              'goToEndorsementFlow',
              'endorsement',
              this.getHelpContext()
            );
            this.portalRedirectService.goToPortalV1('policy_update', referenceAnswer.slug, 'helpflow');
          });
        }
      })
      .catch((err) => {
        this.loggerService.error('HelpService', 'goToEndorsementFlow', { error: err }, currentEventLogic);
      })
  }

  goToSelfService() {
    const helpContext = this.getHelpContext();
    const referenceAnswer = this.getAnswerByStepSlug('reference-list');
    const customerIntent = this.getAnswerByStepSlug('customer-intent');
    const topic = this.getAnswerByStepSlug('topic');

    this.logEvent(
      'off-ramp',
      'goToSelfService',
      customerIntent || ' ',
      helpContext
    );

    switch (customerIntent) {
      case 'policy-documents':
        this.router.navigate(
          [`/policy/${referenceAnswer.class}/${referenceAnswer.slug}`],
          { queryParams: { selfService: 'documents', source: 'help_flow' } }
        );
        break;
      case 'cancel-policy':
        this.userDataService.getUserData()
          .then((response: any) => {
            return this.getFilteredPolicy(referenceAnswer.slug, response.policies);
          })
          .then((policyData) => {
            this.helpflow.selectedPolicyNumber = policyData.policy_number;
            this.helpflow.selectedPolicyProductClass = policyData.product?.class;
            return this.productDetailsService.get(policyData.product.slug);
          })
          .then((productDetails) => {
            const isRaptorPolicy = productDetails.is_raptorial;
            const isAppointmentEnabled = productDetails.is_appointment_enabled;
            const isSelfServicedCancellationEnabled = productDetails.is_self_serviced_cancellation_enabled;

            if (isSelfServicedCancellationEnabled) {
              if (isRaptorPolicy) {
                // Redirect to raptor cancellation flow
                this.resetHelpflow();

                this.showInterstitial('selfService');
                this.interstitialDelay().then(() => this.router.navigate([`/cancellation/${referenceAnswer.slug}`]));
              } else {
                // Redirect to Portal v1's appointment flow to handle cancellation
                const topic = customerIntent.replace('-','_');
                const payload = {
                  ...APPOINTMENT_BOOKING_TOPICS[customerIntent],
                  entity: `${isRaptorPolicy ? 'raptor' : productDetails.class}-policy`,
                  entity_ref: referenceAnswer.slug,
                  option: 'cancellation',
                  source: 'opv2-helpflow',
                  topic
                };

                this.resetHelpflow();
                this.showInterstitial('selfService')
                this.interstitialDelay().then(() => this.portalRedirectService.bookAppointment(topic, payload));
              };
            } else if (isAppointmentEnabled) {
              const payload = {
                ...APPOINTMENT_BOOKING_TOPICS[customerIntent],
                entity: `${isRaptorPolicy ? 'raptor' : productDetails.class}-policy`,
                entity_ref: referenceAnswer.slug,
                option: 'endorsement'
              };

              this.goToBookAppointment(customerIntent, payload);
            } else if (productDetails.class === 'mobile' && this.isIntercomEnabled) {
              // Mobile Customers go to intercom, no cancellation
              this.intercomService.show();
              return;
            } else {
              this.goToContactPage();
            };
          })
        break;
      case 'retry-payment':
        this.resetHelpflow();

        this.router.navigate([`/policy/payment/retry/${referenceAnswer.slug}`]);
        break;
      case 'update-payment':
        this.resetHelpflow();

        this.router.navigate([`/policy/payment/update/${referenceAnswer.slug}`]);
        break;
      case 'start-claim':
        let currentEventLogic = 'getUserData';
        this.userDataService.getUserData()
          .then((response: any) => {
            currentEventLogic = 'getFilteredPolicy';
            return this.getFilteredPolicy(referenceAnswer.slug, response.policies);
          })
          .then((policyData) => {
            this.helpflow.selectedPolicyNumber = policyData.policy_number;
            this.helpflow.selectedPolicyProductClass = policyData.product?.class;
            if (policyData.product?.fnol?.is_using_third_party_fnol) {
              if (
                policyData.product?.fnol?.type === 'third_party' &&
                policyData.product?.fnol?.url
              ) {
                window.open(policyData.product?.fnol?.url, '_blank');
              } else if (
                policyData.product?.fnol?.type === 'intercom' &&
                this.isIntercomEnabled
              ) {
                this.intercomService.show();
              }
            } else {
              if (policyData.product?.class === 'travel') {
                currentEventLogic = 'goToContactPage';

                // Reducing activeStepIndex because going back from contact page in travel scenario
                // we want to see the step claim L2 selection
                this.helpflow.activeStepIndex -= 1;

                this.goToContactPage('travel');
              } else {
                currentEventLogic = 'goToPortalV1ForNewClaim';
                this.resetHelpflow();

                this.showInterstitial('selfService');
                this.interstitialDelay().then(() => this.portalRedirectService.goToPortalV1('new_claim', referenceAnswer.slug, 'helpflow'));
              }
            }
          })
          .catch((err) => {
            this.loggerService.error('HelpService', 'goToEndorsementFlow', { error: err }, currentEventLogic);
          });
        break;
      case 'update-renewal':
        if (topic === 'endorse-renewal') {
          this.router.navigate([`/prerenewal/${referenceAnswer.slug}`]);
        } else {
          this.router.navigate([`/policy/${referenceAnswer.class}/${referenceAnswer.slug}`]);
          this.router.navigate(
            [`/policy/${referenceAnswer.class}/${referenceAnswer.slug}`],
            { queryParams: { source: 'help_flow' } }
          );
        }
        break;
      default:
        if (helpContext.subject === 'odo') {
          this.router.navigate([`/policy/odo/${referenceAnswer.slug}`], { queryParams: { supportFlow: true } });
        }
        break;
    }
  }

  goToBookAppointment(helpType?: string, payload?: IAppointmentDetails, policyDetail?: any) {
    const helpContext = this.getHelpContext();
    const subject = this.getAnswerByStepSlug('subject');
    const referenceAnswer = this.getAnswerByStepSlug('reference-list');
    const isQuoteFlow = helpType === '' && subject === 'quote';
    let bookAppointmentPayload = {} as IAppointmentDetails;

    let intercomData = this.partnerBrandService.getAttribute('intercom', 'partner');
    if (intercomData?.intercomAppId) {
      // Mobile Customers go to intercom, no appointments
      this.intercomService.show();

      if (this.authService.isAuthenticated() && policyDetail) {
        this.router.navigate([`policy/${policyDetail.product?.class}/${policyDetail.uuid || policyDetail.slug}`]);
      } else if (this.authService.isAuthenticated()) {
        this.router.navigate(['/dashboard']);
      } else {
        this.router.navigateByUrl(`/login?partner=${encodeURIComponent(this.authService.getApiKey() || '')}&action=dashboard`);
      }
      return;
    }

    this.logEvent(
      'off-ramp',
      'goToBookAppointment',
      'book-appointment',
      helpContext
    );

    if (payload) {
      bookAppointmentPayload = payload;
    } else if (helpType && helpType.length > 0 && !payload) {
      let entity_ref = referenceAnswer && referenceAnswer.slug;
      let intentOtherTopics: string[] = [];

      // find the 'other' topics to populate the option and entity values
      HELP_CONSTANTS.step_options.forEach(option => {
        if (option.key === 'customer-intent_other') {
          option.choices.forEach((choice) => {
            // excluding financial-difficulties as it is a special case
            if (choice.key !== 'financial-difficulties') {
              intentOtherTopics.push(choice.key);
            }
          })
        }
      });

      if (intentOtherTopics.indexOf(helpType) > -1) {
        bookAppointmentPayload = {
          ...APPOINTMENT_BOOKING_TOPICS.contactUs,
          entity: helpType
        }
      } else if (APPOINTMENT_BOOKING_TOPICS[helpType]) {
        const customerTopic = this.getAnswerByStepSlug('topic');
        bookAppointmentPayload = {
          ...APPOINTMENT_BOOKING_TOPICS[helpType],
          ...(helpType === 'financial-difficulties' ? { option: helpType.replace('-', '_') } : {}),
          ...(
            ['update-renewal', 'update-payment'].indexOf(helpType) > -1 ?
            { entity: `${ policyDetail?.product?.is_raptorial ? 'raptor' : policyDetail?.product?.class }-policy` } :
            {}
          ),
          entity_ref
        }
      } else {
        bookAppointmentPayload = {
          ...APPOINTMENT_BOOKING_TOPICS.general
        }
      }
    } else if (isQuoteFlow && !payload) {
      bookAppointmentPayload = {
        ...APPOINTMENT_BOOKING_TOPICS.quoteFlow,
        ...(referenceAnswer && referenceAnswer.slug ? { entity_ref: referenceAnswer.slug } : {})
      }
    } else if (subject === 'coverage' && !payload) {
      bookAppointmentPayload = {
        ...APPOINTMENT_BOOKING_TOPICS.coverage
      }
    }

    if (this.isRaptorFlow) {
      bookAppointmentPayload['raptorSource'] = this.raptorSource;
    }

    this.resetHelpflow();

    if (
      policyDetail?.product?.is_assist_support_enabled &&
      ASSISTED_SUPPORT_REQUEST_TOPICS.includes(helpContext.intent)
    ) {
      // For existing claims user goes to request assisted support
      this.requestService.start(bookAppointmentPayload);
    } else {
      this.appointmentService.start(bookAppointmentPayload);
    }
  }

  goToContactPage(productClass?: string) {
    // Mobile Customers go to intercom, default action
    if (this.isIntercomEnabled && productClass !== 'travel') {
      this.intercomService.show();
      return;
    }

    // Record index of step leading to help-contact step
    this.helpflow.preContactIndex = this.helpflow.activeStepIndex;

    this.helpflow.activeStepIndex = this.helpflow.steps.findIndex((step: any) => step.slug === 'contact');
    this.saveHelpFlowToSessionStorage();
    this.helpInterstitialService.close();
    this.helpSubject.next(this.helpflow.activeStepIndex);
  }

  getFilteredPolicy(policySlug: string, policies: any) {
    const allPolicies = Object.keys(policies).reduce(
      (acc: any, key: any) => {
        return acc.concat(policies[key]);
      }, []);

    return allPolicies.find((policy: any) => policy.slug === policySlug || policy.uuid === policySlug);
  }

  getOdoRequiredPolicies(policies: any): Promise<any[]> {
    return new Promise(async (resolve, reject) => {
      let needOdoList: any[] = [];
      const eligiblePolicies = policies.filter((policy: any) => this.isPaydPolicyInCoolingPeriod(policy));
      const promises = eligiblePolicies.map(
        (policy: any) =>
          this.odoReadingService.getOdoReading(policy.key)
            .then((response: any) => response)
            .catch((error: any) => {
              // Silently handle errors to not block the UI
              return;
            })
      );
      const results = (await Promise.all(promises)).filter(result => result !== undefined);

      if (results.length > 0) {
        const filteredPolicies = policies.filter((policy: any) => {
          for (const result of results) {
            if (result.policy_number === policy.policyNumber) {
              return policy;
            }
          };
        });
        needOdoList = filteredPolicies;
      }

      this.odoRequiredPolicies = needOdoList;
      resolve(needOdoList);
    });
  }

  goToSmartFlightDelay() {
    const helpContext = this.getHelpContext();

    if (this.helpflow.steps[this.helpflow.activeStepIndex].answer === 'add-flight') {
      this.saveHelpFlowToSessionStorage();

      this.logEvent(
        'off-ramp',
        'goToSmartFlightDelay',
        'smart-flight-delay add-flights',
        helpContext
      );

      // Redirect to external Collinson-managed Smart Flight Delay
      window.open(environment.smartFlightDelay, '_blank');
    } else {
      this.logEvent(
        'off-ramp',
        'goToSmartFlightDelay',
        'smart-flight-delay contact',
        helpContext
      );
      this.goToContactPage();
    }
  }

  saveHelpFlowToSessionStorage() {
    window.sessionStorage.setItem('helpflow', JSON.stringify(this.helpflow));
  }

  removeHelpFlowSessionStorage() {
    window.sessionStorage.removeItem('helpflow');
  }

  interstitialDelay() {
    return new Promise(resolve => setTimeout(resolve, HELP_CONSTANTS.interstitialDuration));
  }

  showInterstitial(messageType: 'selfService' | 'bookAppointment') {
    const messageTable = {
      selfService: {
        heading: "Let\'s help you get this sorted online.",
        subtext: "Manage your insurance 24/7"
      },
      bookAppointment: {
        heading: "Let\'s connect you with an expert.",
        subtext: "Book a time that suits you best."
      }
    }

    window.scrollTo(0, 0);
    this.helpInterstitialService.open({
      'type': "default",
      'settings': {
        'icon': "/assets/svgs/common/loading-blue.svg",
        'time': HELP_CONSTANTS.interstitialDuration
      },
      'heading': messageTable[messageType].heading,
      'subtext': messageTable[messageType].subtext,
    });
  }

  getHelpContext() {
    let subject = this.helpflow.steps.find((steps: any) => steps.slug === 'subject');
    let intent = this.helpflow.steps.find((steps: any) => steps.slug === 'customer-intent');
    let reference = this.helpflow.steps.find((steps: any) => steps.slug === 'reference-list');
    let topic = this.helpflow.steps.find((steps: any) => steps.slug === 'topic');

    return {
      subject: subject && subject.answer,
      intent: intent && intent.answer,
      reference: reference && reference.answer,
      topic: topic && topic.answer
    }
  }

  resetHelpflow() {
    this.helpflow = this.deepCopy(HELP_CONSTANTS.initialSteps);
    window.sessionStorage.removeItem('helpflow');
  }

  private deepCopy(object: any) {
    return JSON.parse(JSON.stringify(object));
  }

  getAnswerByStepSlug(slug: 'subject' | 'customer-intent' | 'reference-list' | 'topic') {
    return this.helpflow.steps.find((step: any) => step.slug === slug)?.answer;
  }

  // Returns true if user has a claim on selected policy in an active state
  //  Only relevant for travel products to change off-ramp
  hasActiveClaimsOnTravel(selectedPolicySlug: string, claims: any) {
    let activeClaims = claims.filter((claim: any) => {
      // Travel claim is considered active if NOT in DRAFT: “D”
      return claim.policy_slug === selectedPolicySlug &&
        claim.product?.class === 'travel' &&
        claim.state !== 'D';
    });

    return activeClaims.length > 0;
  }

  canSelfServeRenewal(policyData: any, isRenewalEnabledForProduct: boolean) {
    const nonSelfRenewableFlags = ['CD', 'OP', 'WH'];
    const topicAnswer = this.helpflow.steps.find((step: any) => step.slug === 'topic')?.answer as any;
    const inRenewalPeriod = policyData.renewal && policyData.renewal.renewal_state === 'active';
    const hasRenewalPermission = nonSelfRenewableFlags.indexOf(policyData.renewal_flag) === -1;

    return topicAnswer === 'endorse-renewal' &&
      policyData.product.is_raptorial &&
      inRenewalPeriod &&
      hasRenewalPermission &&
      isRenewalEnabledForProduct;
  }

  // Returns true if the user has any active PAYD (Pay As You Drive) policy
  hasPaydPolicy(policies: any[]): boolean {
    const paydPolicy = policies.find(policy =>
      policy.class === 'car' &&
      policy.status === 'active' &&
      policy.coverType === 'comp' &&
      policy.coverLevel === 'payd'
    );

    return !!paydPolicy;
  }

  // Determines if policy is PAYD and in cooling period(started in last 14 days)
  isPaydPolicyInCoolingPeriod(policy: any): boolean {
    const timeSincePolicyStartInDays = (Math.abs(Date.now() - Date.parse(policy.startDate)) / (1000 * 3600 * 24));
    return policy.class === 'car' &&
      policy.status === 'active' &&
      policy.coverType === 'comp' &&
      policy.coverLevel === 'payd' &&
      timeSincePolicyStartInDays <= 14
  }

  // User analytics service to create maverick logs and logger service for browser logs
  logEvent(eventName: string, eventMethod: string, eventLabel: string, eventValue?: any) {
    this.analyticsService.trackEvent(
      eventName,
      'helpflow',
      eventLabel,
      { userSlug: this.helpflow.userSlug || this.authService.getLoggedInUser() },
      eventValue
    );
    this.loggerService.info(
      'HelpService',
      eventMethod,
      {
        stepName: eventLabel,
        stepAnswer: eventValue
      },
      eventName
    );
  }
}
